import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { WALLET_DEBITS_PAGE_SIZE } from "../constants/walletDebits.constants";
import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit, WalletDebitStatus } from "../types/walletDebits.types";

export interface UseWalletDebitsResult {
  rows: WalletDebit[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  /** False after a 401/403: the caller renders the no-access view. */
  hasAccess: boolean;
  reload: () => void;
}

interface PageRead {
  key: string;
  rows: WalletDebit[];
  total: number;
  error: string | null;
}

/**
 * The staff history, newest first (pending ones oldest first), optionally for one organization or status.
 * Loading is derived from whether the last answer belongs to the current query, so an answer for an earlier
 * query never lands on screen.
 */
export function useWalletDebits(
  organizationId: string | null,
  status?: WalletDebitStatus,
): UseWalletDebitsResult {
  const [paging, setPaging] = useState<{ organizationId: string | null; page: number }>({
    organizationId,
    page: 1,
  });
  const [nonce, setNonce] = useState(0);
  const [read, setRead] = useState<PageRead | null>(null);
  const [hasAccess, setHasAccess] = useState(true);

  const page = paging.organizationId === organizationId ? paging.page : 1;
  const key = `${organizationId ?? "all"}:${status ?? "any"}:${page}:${nonce}`;

  useEffect(() => {
    let cancelled = false;
    WalletDebitsService.list({
      ...(organizationId ? { organization_id: organizationId } : {}),
      ...(status ? { status } : {}),
      skip: (page - 1) * WALLET_DEBITS_PAGE_SIZE,
      limit: WALLET_DEBITS_PAGE_SIZE,
    })
      .then((result) => {
        if (!cancelled) setRead({ key, rows: result.data, total: result.total, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(err)) setHasAccess(false);
        setRead((previous) => ({
          key,
          rows: previous?.rows ?? [],
          total: previous?.total ?? 0,
          error: HttpApiService.getErrorMessage(err),
        }));
      });
    return () => {
      cancelled = true;
    };
  }, [key, organizationId, page, status]);

  const setPage = useCallback((next: number) => setPaging({ organizationId, page: next }), [organizationId]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    rows: read?.rows ?? [],
    total: read?.total ?? 0,
    page,
    setPage,
    isLoading: read?.key !== key,
    error: read?.key === key ? read.error : null,
    hasAccess,
    reload,
  };
}
